import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiRequest } from "./api";
import type { components } from "./api-types";

/**
 * v2.10 P1.2 (D-133): every dial goes through Google Voice. The number itself opens Voice's own "Call …?
 * Cancel / Call" dialog (D-128) in ONE reused window (`cornerstone-voice`), in hello@'s account — by
 * `u/N` when VOICE_ACCOUNT_INDEX is set, else `authuser=<the Voice account>`, which does not depend on the
 * order a device signed accounts in. `tel:` (the carrier line, which shows the cell's 201 number and that
 * Voice never sees) is only ever behind ⋯ → "Call with phone — shows your cell number".
 *
 * Nothing here places a call: opening the dialog is a click, the call is Sam's press of Call in Voice.
 */
type S = components["schemas"];
export type VoiceConfig = S["VoiceConfig"];
export type VoiceToday = S["VoiceToday"];
export type VoiceQueueRow = S["VoiceQueueRow"];
export type DialPoll = S["DialPoll"];

export const VOICE_WINDOW = "cornerstone-voice";

const DEFAULTS: Pick<VoiceConfig, "voice_base" | "voice_authuser"> = {
  voice_base: "https://voice.google.com",
  voice_authuser: "hello@cstlegacy.com",
};
let current: Pick<VoiceConfig, "voice_base" | "voice_authuser"> = DEFAULTS;

/** Set once the API's /voice-config is loaded (App), so every link built after it targets the account. */
export function setVoiceAccount(cfg: Pick<VoiceConfig, "voice_base" | "voice_authuser"> | null): void {
  current = cfg ? { voice_base: cfg.voice_base, voice_authuser: cfg.voice_authuser ?? null } : DEFAULTS;
}

function withAccount(url: string): string {
  const who = current.voice_authuser;
  if (!who) return url;
  return `${url}${url.includes("?") ? "&" : "?"}authuser=${encodeURIComponent(who).replace("%40", "@")}`;
}

/** Voice's "new call" dialog with the number filled in. Opening it dials nothing. */
export function voiceDialUrl(e164: string): string {
  return withAccount(`${current.voice_base}/calls?a=nc,${encodeURIComponent(e164)}`);
}

/** Voice's call list. */
export function voiceCallsUrl(): string {
  return withAccount(`${current.voice_base}/calls`);
}

/** T2 (P3.2): the Voice web conversation with this number (the itemId form Voice uses in its own links). */
export function voiceMessagesUrl(e164: string): string {
  return withAccount(`${current.voice_base}/messages?itemId=t.${encodeURIComponent(e164)}`);
}

/** Open (or reuse) the one Voice window. Returns false when the browser blocked it. */
export function openVoice(url: string): boolean {
  const w = window.open(url, VOICE_WINDOW);
  return w !== null;
}

export type DialContext = { leadId?: string | null; dealId?: string | null; surface?: string };

/** P1.2: pressing a number is recorded (P2.5 waits for that call's Voice event). Fire and forget. */
export function recordDial(e164: string, ctx: DialContext = {}): void {
  void apiRequest("/api/v1/telephony/dial-clicks", {
    method: "POST",
    body: JSON.stringify({
      phone_e164: e164,
      lead_id: ctx.leadId ?? null,
      deal_id: ctx.dealId ?? null,
      surface: ctx.surface ?? null,
    }),
  }).catch(() => undefined);
}

export const voice = {
  config: () => apiRequest<VoiceConfig>("/api/v1/telephony/voice-config"),
  today: () => apiRequest<VoiceToday>("/api/v1/telephony/today"),
  confirm: (touchIds: string[]) =>
    apiRequest<{ confirmed: number }>("/api/v1/telephony/confirm", {
      method: "POST",
      body: JSON.stringify({ touch_ids: touchIds }),
    }),
  poll: (phone: string, since: string) =>
    apiRequest<DialPoll>(
      `/api/v1/telephony/dial-poll?phone=${encodeURIComponent(phone)}&since=${encodeURIComponent(since)}`,
    ),
};

/** Loads /voice-config once per session and points every Voice link at the right account. */
export function useVoiceConfig(enabled = true) {
  const q = useQuery({ queryKey: ["voice-config"], queryFn: voice.config, staleTime: 600_000, enabled });
  useEffect(() => {
    if (q.data) setVoiceAccount(q.data);
  }, [q.data]);
  return q.data;
}

/** Today's Voice lists (awaiting confirm, callbacks, texts to send), refreshed every minute. */
export function useVoiceToday() {
  return useQuery({ queryKey: ["voice-today"], queryFn: voice.today, refetchInterval: 60_000 });
}

/** "VM? 0:48", "Conn? 3:12", "No answer 0:00" — an auto outcome's pill text (P2.4). */
export function formatClock(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(seconds ?? 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
